var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { differenceInCalendarDays } from 'date-fns';
import { attentionRequiredStatus, autopayScheduledStatus, closedWithBalanceStatus, currentPaymentDueStatus, enrolledOnPaymentPlanStatus, futurePaymentDueStatus, getPaymentProcessingStatus, getScheduledPaymentStatus, loanPaidOffStatus, noPaymentDueStatus, pastDueStatus, registerAccountStatus, waivedInterestExpiredNotification, waivedInterestExpiringNotification } from './notificationStatus';
import { getNumberOfDaysToAutopay, getNumberOfDaysToScheduledPayment, getProcessingActivities, getScheduledActivities, numberOfDaysToDueDate } from './activityHelpers';
import { INSTALLMENT_LOAN, INSTALLMENT_LOAN_MONTHLY, INSTALLMENT_LOAN_PAY_IN_FOUR, WAIVED_INTEREST } from '../const/products';
import getScheduledAutopayAmount from './getScheduledAutopayAmount';
// eslint-disable-next-line max-lines-per-function
var getNotifications = function (accountDetails) {
    var activity = accountDetails.activity, billing = accountDetails.billing, conditions = accountDetails.conditions, credit = accountDetails.credit, isRegistered = accountDetails.isRegistered, product = accountDetails.product, enrollments = accountDetails.enrollments;
    var _a = billing || {}, pastDue = _a.pastDue, nextPaymentDate = _a.nextPaymentDate, totalMinimumPaymentDue = _a.totalMinimumPaymentDue, customerAssistancePlan = _a.customerAssistancePlan, settlementEnrolled = _a.settlementEnrolled, autopayEnrollment = _a.autopayEnrollment, waivedInterest = _a.waivedInterest;
    var daysUntilNextPaymentDue = numberOfDaysToDueDate(nextPaymentDate);
    var isClosedWithBalance = conditions === null || conditions === void 0 ? void 0 : conditions.includes('CLOSED_WITH_BALANCE');
    var isDelinquent = conditions === null || conditions === void 0 ? void 0 : conditions.includes('DELINQUENT');
    var isAttentionRequired = conditions === null || conditions === void 0 ? void 0 : conditions.includes('AUTH_PROHIBITED');
    var isOpen = conditions === null || conditions === void 0 ? void 0 : conditions.includes('OPEN');
    var isWaivedInterest = (enrollments === null || enrollments === void 0 ? void 0 : enrollments.includes(WAIVED_INTEREST)) &&
        product === INSTALLMENT_LOAN_MONTHLY;
    var waivedInterestPeriodDaysLeft = differenceInCalendarDays(new Date(waivedInterest === null || waivedInterest === void 0 ? void 0 : waivedInterest.promoEndDate), new Date());
    var isWaivedInterestExpired = waivedInterestPeriodDaysLeft < 0;
    var isWaivedInterestExpiring = waivedInterestPeriodDaysLeft <= 30 && waivedInterestPeriodDaysLeft >= 0;
    var isOpenOrClosedWithBalance = isOpen || isClosedWithBalance;
    var isSetpayAccount = product === INSTALLMENT_LOAN;
    var isPayLaterAccount = product === INSTALLMENT_LOAN_MONTHLY ||
        product === INSTALLMENT_LOAN_PAY_IN_FOUR;
    var isRegisteredAuthProhibited = isAttentionRequired && isRegistered;
    var isEnrolledInPlans = customerAssistancePlan || settlementEnrolled;
    var isNotEnrolledInPlans = !customerAssistancePlan && !settlementEnrolled;
    var isPastDueAndRegistered = pastDue && isRegistered;
    var isPastDueAndUnregistered = pastDue && !isRegistered;
    var isOpenOrClosedEnrolledInPlans = isOpenOrClosedWithBalance && isEnrolledInPlans;
    var isOpenOrClosedNotEnrolledInPlans = isOpenOrClosedWithBalance && isNotEnrolledInPlans;
    var isRegisteredNotEnrolled = isPastDueAndRegistered && isDelinquent && isOpenOrClosedNotEnrolledInPlans;
    var isRegisteredEnrolled = isPastDueAndRegistered && isOpenOrClosedEnrolledInPlans;
    var isUnregisteredNotEnrolled = isPastDueAndUnregistered && isOpenOrClosedNotEnrolledInPlans;
    var isUnregisteredEnrolled = isPastDueAndUnregistered && isOpenOrClosedEnrolledInPlans;
    var isEnrolledNotDelinquent = isOpenOrClosedEnrolledInPlans && !isDelinquent;
    var scheduledAutopayAmount = getScheduledAutopayAmount(accountDetails, isSetpayAccount);
    var isAutopayEnrolled = Number(scheduledAutopayAmount) > 0 && autopayEnrollment;
    var isNoPaymentDue = !pastDue &&
        totalMinimumPaymentDue === '0.00' &&
        (isOpenOrClosedWithBalance || isAttentionRequired);
    var isPaymentDueToday = !pastDue &&
        daysUntilNextPaymentDue === 0 &&
        !(isAutopayEnrolled || isNoPaymentDue);
    var isFuturePaymentDue = !pastDue &&
        daysUntilNextPaymentDue > 0 &&
        !(isAutopayEnrolled || isNoPaymentDue);
    var isPaymentPastDue = daysUntilNextPaymentDue < 0;
    var isCurrentBalanceZero = Number(credit === null || credit === void 0 ? void 0 : credit.currentBalance) <= 0;
    var scheduledActivities = getScheduledActivities(activity);
    var scheduledPaymentStatus = getScheduledPaymentStatus(scheduledActivities, isPayLaterAccount);
    var isAutopayDisplayOnPriority = scheduledActivities.some(function (scheduledActivity) {
        return getNumberOfDaysToScheduledPayment(scheduledActivity) <
            getNumberOfDaysToAutopay(nextPaymentDate);
    });
    var processingActivities = getProcessingActivities(activity);
    var paymentProcessingStatus = getPaymentProcessingStatus(processingActivities);
    var autopayScheduledToday = isAutopayEnrolled && getNumberOfDaysToAutopay(nextPaymentDate) === 0;
    var isPaymentScheduledToday = scheduledActivities.some(function (scheduledActivity) {
        return getNumberOfDaysToScheduledPayment(scheduledActivity) === 0;
    }) || autopayScheduledToday;
    var isCurrentPaymentDue = isPaymentDueToday && !isPaymentScheduledToday;
    var pastDueNotification = [
        isPastDueAndRegistered,
        isRegisteredEnrolled,
        isRegisteredNotEnrolled,
        isUnregisteredNotEnrolled,
        isUnregisteredEnrolled
    ];
    var isPastDue = pastDueNotification.some(function (pastDueNotify) { return pastDueNotify === true; });
    var enrolledInPlanNotification = [
        isRegisteredEnrolled,
        isUnregisteredEnrolled,
        isEnrolledNotDelinquent
    ];
    var isEnrolledInPaymentPlan = enrolledInPlanNotification.some(function (enrolled) { return enrolled === true; });
    var notificationsStatus;
    var isAutopayDateValid = isAutopayEnrolled && !isPaymentPastDue;
    if (isCurrentBalanceZero && isPayLaterAccount) {
        notificationsStatus = [loanPaidOffStatus()];
    }
    else if (isCurrentBalanceZero && isSetpayAccount) {
        notificationsStatus = !isRegistered
            ? [loanPaidOffStatus(), registerAccountStatus()]
            : [loanPaidOffStatus()];
    }
    else if (isCurrentBalanceZero && !isPayLaterAccount) {
        notificationsStatus = [
            isRegisteredAuthProhibited && attentionRequiredStatus(),
            isEnrolledInPaymentPlan && enrolledOnPaymentPlanStatus(),
            isNoPaymentDue && noPaymentDueStatus(),
            !isRegistered && registerAccountStatus(),
            isClosedWithBalance && closedWithBalanceStatus()
        ];
    }
    else if (isAutopayDisplayOnPriority) {
        notificationsStatus = __spreadArray(__spreadArray(__spreadArray([
            isWaivedInterest &&
                isWaivedInterestExpired &&
                waivedInterestExpiredNotification(),
            isWaivedInterest &&
                isWaivedInterestExpiring &&
                waivedInterestExpiringNotification(waivedInterest),
            isRegisteredAuthProhibited &&
                !isPayLaterAccount &&
                attentionRequiredStatus(),
            isPastDue && pastDueStatus(isPayLaterAccount),
            isEnrolledInPaymentPlan &&
                !isPayLaterAccount &&
                enrolledOnPaymentPlanStatus(),
            isCurrentPaymentDue && currentPaymentDueStatus(),
            isFuturePaymentDue && futurePaymentDueStatus(nextPaymentDate)
        ], paymentProcessingStatus, true), scheduledPaymentStatus, true), [
            isAutopayDateValid &&
                autopayScheduledStatus(scheduledAutopayAmount, getNumberOfDaysToAutopay(nextPaymentDate)),
            isNoPaymentDue && !isPayLaterAccount && noPaymentDueStatus(),
            !isRegistered && !isPayLaterAccount && registerAccountStatus(),
            isClosedWithBalance && !isPayLaterAccount && closedWithBalanceStatus()
        ], false);
    }
    else {
        notificationsStatus = __spreadArray(__spreadArray(__spreadArray(__spreadArray([
            isWaivedInterest &&
                isWaivedInterestExpired &&
                waivedInterestExpiredNotification(),
            isWaivedInterest &&
                isWaivedInterestExpiring &&
                waivedInterestExpiringNotification(waivedInterest),
            isRegisteredAuthProhibited &&
                !isPayLaterAccount &&
                attentionRequiredStatus(),
            isPastDue && pastDueStatus(isPayLaterAccount),
            isEnrolledInPaymentPlan &&
                !isPayLaterAccount &&
                enrolledOnPaymentPlanStatus(),
            isCurrentPaymentDue && currentPaymentDueStatus(),
            isFuturePaymentDue && futurePaymentDueStatus(nextPaymentDate)
        ], paymentProcessingStatus, true), [
            isAutopayDateValid &&
                autopayScheduledStatus(scheduledAutopayAmount, getNumberOfDaysToAutopay(nextPaymentDate))
        ], false), scheduledPaymentStatus, true), [
            isNoPaymentDue && !isPayLaterAccount && noPaymentDueStatus(),
            !isRegistered && !isPayLaterAccount && registerAccountStatus(),
            isClosedWithBalance && !isPayLaterAccount && closedWithBalanceStatus()
        ], false);
    }
    return notificationsStatus.filter(function (eachStatus) { return eachStatus; });
};
export default getNotifications;
